import React from "react";
import { withStyles } from "@material-ui/core";
import Button from '@material-ui/core/Button';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import PropTypes from "prop-types";
import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';
import RouterIcon from '@material-ui/icons/Router';
import TuneIcon from '@material-ui/icons/Tune';
import moment from "moment";
import * as helper from "../common/helper";
import SelectTable from './SelectTable';
import * as i18n from "../common/i18n";
import * as graphql from '../common/graphql';
import gql from 'graphql-tag';
import Message from "./Message";
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';

import "moment/locale/de";

moment.locale("de");

const styles = theme => ({
    root: {
        width: '100%',
        maxWidth: 360,
        backgroundColor: theme.palette.background.paper,
    },
    inline: {
        display: 'inline',
    },
    margin: {
        margin: theme.spacing(1),
    },
    withoutLabel: {
        marginTop: theme.spacing(3),
    },
    textField: {
        width: 200,
    },
});

const factorySettings = {
    endpoints: [
        "https://www.mhs-wasser.de",
        "https://p71-prod.credeo.io"
    ],
    freq: 30,
    sWait: 259200,
    sHeartbeat: 86400,
    ringBuffer: 10,
    msMin: 120000,
    msMax: 600000,
    mlMin: 15000,
    mlMax: 40000,
    mmMax: 100,
    msLog: 1000,
    threshold: 15
};

class ConfigurationWizard extends React.Component {
    constructor(props) {

        super(props);

        this.state = {
            message: null,
            activeStep: 0,
            configuration: null,
            selectedConfiguration: null,
            selectedConfigurationDetails: null,
            templateMode: "0",
            steps: this.getSteps(),
            loading: false,
            details: null,
            hideAfter: 3000,
            variant: "info",
            search: "",
        };
    }

    showMessage = (message, variant, details, hideAfter) => {
        this.setState({
            message: message,
            variant: variant ? variant : "info",
            hideAfter: hideAfter ? hideAfter : 3000,
            details: details ? details : null,
        })
    }

    onModeChange = async (event) => {
        await this.setState({
            templateMode: event.target.value,
        });
    }

    onAddConfiguration = async (data) => {
        await this.setState({
            selectedConfiguration: data.id,
            selectedConfigurationDetails: data,
        });
    }

    onDeleteConfiguration = (data) => {
        this.setState({
            selectedConfiguration: null,
            selectedConfigurationDetails: {},
        });
    }

    onClose = () => {
        this.props.onCancel();
        this.reset();
    }

    onPrevious = () => {
        const activeStep = this.state.activeStep - 1;
        this.setState({ activeStep: activeStep })
    }

    onNext = async () => {
        this.setState({ loading: true })
        const activeStep = this.state.activeStep + 1;

        if (activeStep === 1) {
            if (this.state.templateMode === "0") {
                await this.fetchCurrentConfiguration(this.props.data.device.uuid)
            }
            else if (this.state.templateMode === "1") {
                await this.fetchCurrentVersion(this.props.data.device.uuid)
                const configuration = factorySettings
                configuration["uuid"] = this.props.data.device.uuid
                configuration["created"] = moment().unix()
                configuration["version"] = this.state.currentVersion + 1
                configuration["id"] = `${configuration["uuid"]}-${configuration["version"]}`
                await this.setState({ configuration: configuration })
            }
            else if (this.state.templateMode === "2") {
                await this.fetchCurrentVersion(this.props.data.device.uuid)
                const configuration = this.state.selectedConfigurationDetails
                configuration["uuid"] = this.props.data.device.uuid
                configuration["created"] = moment().unix()
                configuration["version"] = this.state.currentVersion + 1
                configuration["id"] = `${configuration["uuid"]}-${configuration["version"]}`
                await this.setState({ configuration: configuration })
            }
        }
        this.setState({ loading: false })
        this.setState({ activeStep: activeStep })
    }

    nextPossible = () => {
        switch (this.state.activeStep) {
            case 0:
                if (this.state.templateMode === "2") {
                    return this.state.selectedConfiguration !== null
                } else {
                    return true
                }

            default:
                return true;
        }
    }

    onSave = () => {
        this.props.onSave(
            this.state.configuration
        );
        this.onClose();
    }

    reset = () => {
        this.setState(
            {
                message: null,
                activeStep: 0,
                configuration: null,
                selectedConfiguration: null,
                selectedConfigurationDetails: null,
                templateMode: "0",
                steps: this.getSteps(),
                details: null,
                hideAfter: 3000,
                variant: "info",
                search: "",
            }
        )
    }

    closeMessage = () => {
        this.setState({
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        })
    }

    getSteps = () => {
        return [i18n.get("configurationWizard.step.0"), i18n.get("configurationWizard.step.1"), i18n.get("configurationWizard.step.2")];
    }

    getStepContent = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                return this.selectTemplate();
            case 1:
                return this.editConfig();
            case 2:
                return this.showSummary();
            default:
                return 'Error';
        }
    }

    mapField(column) {
        switch (column) {
            case "device":
                return "uuid"
            default:
                return column;
        }
    }

    selectTemplate = () => {

        const device = this.props.data ? this.props.data.device.uuid : null

        return (
            <React.Fragment>
                <CssBaseline />
                <Container maxWidth="lg">
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Vorlage auswählen</FormLabel>
                        <RadioGroup aria-label="mode" name="mode1" value={this.state.templateMode} onChange={this.onModeChange}>
                            <FormControlLabel value="0" control={<Radio />} label="Mit aktueller Konfiguration als Vorlage starten" />
                            <FormControlLabel value="1" control={<Radio />} label="Mit Standardwerten starten" />
                            <FormControlLabel value="2" control={<Radio />} label="Bestehende Konfiguration als Vorlage laden" />
                        </RadioGroup>
                    </FormControl>

                    {this.state.templateMode === "2" ?
                        <div>
                            <br /><Divider variant="middle" /><br />
                            <SelectTable
                                noneText={"Keine Konfiguration ausgewählt. Wählen Sie eine Konfiguration aus der Liste aus"}
                                title1={"Ausgewählte Konfiguration"}
                                title2={"Vorhandene Konfigurationen"}
                                columns={[
                                    {
                                        title: "Geräte-ID",
                                        hidden: false,
                                        filtering: true,
                                        field: "device",
                                        cellStyle: {
                                            minWidth: '200px',
                                            maxWidth: '200px',
                                        },
                                        headerStyle: {
                                            minWidth: '200px',
                                            maxWidth: '200px',
                                        },
                                        render: rowData => {
                                            return rowData && rowData.device ? <Chip label={rowData.device.uuid} icon={<RouterIcon />} /> : null
                                        }
                                    },
                                    {
                                        title: "Version",
                                        hidden: false,
                                        filtering: true,
                                        type: "numeric",
                                        comparator: "IS",
                                        field: "version",
                                        cellStyle: {
                                            minWidth: '200px',
                                            maxWidth: '200px',
                                        },
                                        headerStyle: {
                                            minWidth: '200px',
                                            maxWidth: '200px',
                                        },
                                    },
                                    {
                                        title: "Erstellt am",
                                        hidden: false,
                                        filtering: true,
                                        type: "datetime",
                                        filterComponent: helper.DateRangeFilter,
                                        cellStyle: {
                                            minWidth: '400px',
                                            maxWidth: '400px',
                                        },
                                        headerStyle: {
                                            minWidth: '400px',
                                            maxWidth: '400px',
                                        },
                                        field: "created"
                                    },

                                ]}
                                detailPanel={
                                    rowData => {
                                        return (
                                            <div>
                                                <Paper>
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell align="center">{i18n.get("configurations.sWait")}</TableCell>
                                                                <TableCell align="center">{i18n.get("configurations.sHeartbeat")}</TableCell>
                                                                <TableCell align="center">{i18n.get("configurations.mlMax")}</TableCell>
                                                                <TableCell align="center">{i18n.get("configurations.mlMin")}</TableCell>
                                                                <TableCell align="center">{i18n.get("configurations.mmMax")}</TableCell>
                                                                <TableCell align="center">{i18n.get("configurations.msMin")}</TableCell>
                                                                <TableCell align="center">{i18n.get("configurations.msMax")}</TableCell>
                                                                <TableCell align="center">{i18n.get("configurations.threshold")}</TableCell>
                                                                <TableCell align="center">{i18n.get("configurations.ringBuffer")}</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            <TableRow key={rowData.id}>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.sWait} </TableCell>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.sHeartbeat} </TableCell>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.mlMax} </TableCell>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.mlMin} </TableCell>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.mmMax} </TableCell>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.msMin} </TableCell>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.msMax} </TableCell>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.threshold} </TableCell>
                                                                <TableCell align="center" component="th" scope="row"> {rowData.ringBuffer} </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </Paper>
                                            </div>
                                        );
                                    }
                                }
                                chipIcon={<TuneIcon />}
                                chipLabel={() => { return this.state.selectedConfiguration }}
                                selected={this.state.selectedConfiguration}
                                filtering={true}
                                data={query =>
                                    new Promise(async (resolve, reject) => {

                                        try {

                                            const orderBy = helper.orderByString(query, this.mapField, "uuid", true);
                                            const where = helper.whereString(query["filters"], this.mapField, ["uuid"]);
                                            const result = await graphql.clientWithToken(this.props.accessToken).query({
                                                query: gql`
                                                    query {
                                                        configurations(first: ${query.pageSize}, offset: ${query.pageSize * query.page}, orderBy:${orderBy}, where:${where}) {
                                                            totalCount
                                                            elements {
                                                            ... on Configuration {
                                                                id
                                                                device { uuid }
                                                                version
                                                                created { isoString }
                                                                sWait
                                                                sHeartbeat
                                                                mlMax
                                                                mlMin
                                                                mmMax
                                                                msMax
                                                                msMin
                                                                msLog
                                                                freq
                                                                threshold
                                                                ringBuffer
                                                                endpoints { address }
                                                                }
                                                            }
                                                        }
                                                    }
                                                `,
                                                fetchPolicy: "no-cache"
                                            });

                                            resolve({
                                                page: query.page,
                                                data: result.data.configurations.elements.map(
                                                    (element) => {
                                                        return {
                                                            id: element.id,
                                                            device: element.device,
                                                            version: element.version,
                                                            created: element.created ? moment(element.created.isoString).toDate() : null,
                                                            sWait: element.sWait,
                                                            sHeartbeat: element.sHeartbeat,
                                                            mlMax: element.mlMax,
                                                            mlMin: element.mlMin,
                                                            mmMax: element.mmMax,
                                                            msMax: element.msMax,
                                                            msMin: element.msMin,
                                                            msLog: element.msLog,
                                                            freq: element.freq,
                                                            threshold: element.threshold,
                                                            ringBuffer: element.ringBuffer,
                                                            endpoints: element.endpoints ? element.endpoints.map(endpoint => endpoint["address"]) : [],
                                                        };
                                                    }),
                                                totalCount: result.data.configurations.totalCount
                                            });
                                        } catch (error) {
                                            this.showMessage(error.message, "error");
                                            reject(error);
                                        }
                                    })
                                }
                                actions={[]}
                                onAdd={this.onAddConfiguration}
                                onDelete={this.onDeleteConfiguration}
                                onCancel={this.onClose}
                            />
                        </div>
                        : null}


                </Container>
            </React.Fragment>
        )
    }

    handleCurrentConfigurationChange = async (configuration) => {
        configuration["created"] = moment().unix()
        configuration["version"] = configuration["version"] + 1
        configuration["uuid"] = this.props.data.device.uuid
        configuration["endpoints"] = configuration["endpoints"].map(config => config["address"])
        delete configuration["device"]
        configuration["id"] = `${configuration["uuid"]}-${configuration["version"]}`
        await this.setState({
            configuration: configuration,
        })

    }

    fetchCurrentConfiguration = async () => {
        try {
            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
                    query {
                        configurations(
                            first: 1
                            where: [{ column: "uuid", comparator: IS, string: "${this.props.data.device.uuid}" }]
                            orderBy: [{ column: "version", direction: DESC }]
                        ) {
                            totalCount
                            elements {
                            ... on Configuration {
                                id,
                                device { uuid }
                                version
                                endpoints { address }
                                freq
                                mlMax
                                mlMin
                                mmMax
                                msLog
                                msMax
                                msMin
                                ringBuffer
                                sWait
                                sHeartbeat
                                threshold
                                created { X }
                            }
                        }
                    }
                }`,
                fetchPolicy: "no-cache"
            });


            await this.handleCurrentConfigurationChange(result.data.configurations.elements[0]);
        }
        catch (error) {
            this.showMessage(error.message, "error");
        }
    }

    handleCurrentVersionChange = async (configuration) => {
        await this.setState({
            currentVersion: configuration.version,
        })

    }

    fetchCurrentVersion = async () => {
        try {
            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
                    query {
                        configurations(
                            first: 1
                            where: [{ column: "uuid", comparator: IS, string: "${this.props.data.device.uuid}" }]
                            orderBy: [{ column: "version", direction: DESC }]
                        ) {
                            totalCount
                            elements {
                            ... on Configuration {
                                version
                            }
                        }
                    }
                }`,
                fetchPolicy: "no-cache"
            });


            await this.handleCurrentVersionChange(result.data.configurations.elements[0]);
        }
        catch (error) {
            this.showMessage(error.message, "error");
        }
    }

    handleTextFieldChange = async (event) => {
        const configuration = this.state.configuration
        configuration[event.target.name] = parseInt(event.target.value)
        this.setState({
            configuration: configuration 
        })
    }

    editConfig = () => {
        const data = [
            { label: i18n.get("configurations.sWait"), value: `${this.state.configuration.sWait}`, name: "sWait" },
            { label: i18n.get("configurations.sHeartbeat"), value: `${this.state.configuration.sHeartbeat || 0}`, name: "sHeartbeat" },
            { label: i18n.get("configurations.mlMax"), value: `${this.state.configuration.mlMax}`, name: "mlMax" },
            { label: i18n.get("configurations.mlMin"), value: `${this.state.configuration.mlMin}`, name: "mlMin" },
            { label: i18n.get("configurations.mmMax"), value: `${this.state.configuration.mmMax}`, name: "mmMax" },
            { label: i18n.get("configurations.msMin"), value: `${this.state.configuration.msMin}`, name: "msMin" },
            { label: i18n.get("configurations.msMax"), value: `${this.state.configuration.msMax}`, name: "msMax" },
            { label: i18n.get("configurations.msLog"), value: `${this.state.configuration.msLog}`, name: "msLog" },
            { label: i18n.get("configurations.freq"), value: `${this.state.configuration.freq}`, name: "freq" },
            { label: i18n.get("configurations.ringBuffer"), value: `${this.state.configuration.ringBuffer}`, name: "ringBuffer" },
            { label: i18n.get("configurations.threshold"), value: `${this.state.configuration.threshold}`, name: "threshold" },
        ]

        return (
            <React.Fragment>
                <CssBaseline />
                <Container maxWidth="md">
                    <Grid
                        container={true}
                        spacing={1}
                        justify={'center'}
                    >
                        {data && data.map(d =>
                            <React.Fragment>
                                <Grid item xs={12} lg={6}>
                                    <TextField
                                        id="standard-basic"
                                        label={d.label}
                                        defaultValue={d.value}
                                        name={d.name}
                                        type="number"
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                        onChange={this.handleTextFieldChange}
                                    />
                                </Grid>
                            </React.Fragment>
                        )}
                    </Grid>
                </Container>
            </React.Fragment>
        )
    }

    showSummary = () => {

        const data = [
            { label: i18n.get("configurations.device"), value: `${this.state.configuration.uuid}` },
            { label: i18n.get("configurations.created"), value: `${moment(this.state.configuration.created, 'X').format("DD.MM.YYYY, HH:mm:ss")}` },
            { label: i18n.get("configurations.version"), value: `${this.state.configuration.version}` },
            { label: i18n.get("configurations.sWait"), value: `${this.state.configuration.sWait}` },
            { label: i18n.get("configurations.sHeartbeat"), value: `${this.state.configuration.sHeartbeat}` },
            { label: i18n.get("configurations.mlMax"), value: `${this.state.configuration.mlMax}` },
            { label: i18n.get("configurations.mlMin"), value: `${this.state.configuration.mlMin}` },
            { label: i18n.get("configurations.mmMax"), value: `${this.state.configuration.mmMax}` },
            { label: i18n.get("configurations.msMin"), value: `${this.state.configuration.msMin}` },
            { label: i18n.get("configurations.msMax"), value: `${this.state.configuration.msMax}` },
            { label: i18n.get("configurations.msLog"), value: `${this.state.configuration.msLog}` },
            { label: i18n.get("configurations.freq"), value: `${this.state.configuration.freq}` },
            { label: i18n.get("configurations.threshold"), value: `${this.state.configuration.threshold}` },
            { label: i18n.get("configurations.ringBuffer"), value: `${this.state.configuration.ringBuffer}` },
            { label: i18n.get("configurations.endpoints"), value: this.state.configuration.endpoints.map(endpoint => <Chip label={endpoint} style={{ marginRight: 8, marginBottom: 8 }} />) },
        ]

        return (
            <React.Fragment>
                <CssBaseline />
                <Container maxWidth="md">

                    <Grid
                        container={true}
                        spacing={1}
                        justify={'center'}
                    >

                        {data && data.map(d =>
                            <React.Fragment>
                                <Grid item xs={12} lg={6}>
                                    <b>{d.label}</b>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    {d.value}
                                </Grid>
                            </React.Fragment>
                        )}

                    </Grid>

                </Container>
            </React.Fragment>

        )
    }

    render() {
        return (
            <Dialog
                maxWidth={"lg"}
                fullWidth={true}
                open={this.props.open}
                keepMounted
                disableBackdropClick={true}
                onClose={this.onClose}
                fullScreen={false}
            >

                <DialogTitle>
                    <React.Fragment>
                        {i18n.get("configurationWizard.title")}
                        <Stepper activeStep={this.state.activeStep}>
                            {this.state.steps.map(label => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </React.Fragment>
                </DialogTitle>
                <DialogContent>
                    {this.state.loading ?
                        <div align="center"><CircularProgress /></div>
                        :
                        this.getStepContent(this.state.activeStep)
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.onClose} color="primary">
                        {i18n.get("configurationWizard.cancel")}
                    </Button>
                    {this.state.activeStep > 0 &&
                        <Button onClick={this.onPrevious} color="primary">
                            {i18n.get("configurationWizard.previous")}
                        </Button>
                    }
                    {this.state.activeStep >= 0 && this.state.activeStep < (this.state.steps.length - 1) &&
                        <Button onClick={this.onNext} color="primary" disabled={!this.nextPossible()}>
                            {i18n.get("configurationWizard.next")}
                        </Button>
                    }
                    {this.state.activeStep === (this.state.steps.length - 1) &&
                        <Button onClick={this.onSave} color="primary">
                            {i18n.get("configurationWizard.save")}
                        </Button>
                    }
                </DialogActions>

                <Message
                    hideAfter={this.state.hideAfter}
                    onClose={this.closeMessage}
                    variant={this.state.variant}
                    message={this.state.message}
                    details={this.state.details}
                    open={this.state.message !== null}
                />
            </Dialog>
        );
    }
}

ConfigurationWizard.propTypes = {
    classes:
        PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(ConfigurationWizard)
